/**
 * Wire letterhead-family prints onto the shared pagination engine.
 * Keeps legacy components available as *Legacy exports where needed.
 */
import React from 'react';
import { GenericPaginatedDocument } from '@/components/print/GenericPaginatedDocument';
import {
  buildDeliveryNotePrintDocument,
  buildPackingListPrintDocument,
  buildCommercialInvoicePrintDocument,
} from '@/print-engine/adapters/delivery-adapter';
import {
  buildPurchaseOrderPrintDocument,
  buildSarPrintDocument,
} from '@/print-engine/adapters/procurement-letterhead-adapter';
import { buildSalesInvoicePrintDocument } from '@/print-engine/adapters/sales-invoice-adapter';
import {
  buildCprLineLabelDocument,
  buildCprPrintDocument,
  buildRequirementPrintDocument,
  buildReturnPrintDocument,
  buildReconciliationPrintDocument,
} from '@/print-engine/adapters/cspm-adapter';

export {
  buildDeliveryNotePrintDocument,
  buildPackingListPrintDocument,
  buildCommercialInvoicePrintDocument,
  buildPurchaseOrderPrintDocument,
  buildSarPrintDocument,
  buildSalesInvoicePrintDocument,
  buildCprPrintDocument,
  buildCprLineLabelDocument,
  buildRequirementPrintDocument,
  buildReturnPrintDocument,
  buildReconciliationPrintDocument,
};

export function EngineDeliveryNotePrint(props: {
  data: Parameters<typeof buildDeliveryNotePrintDocument>[0];
  companyInfo?: Parameters<typeof buildDeliveryNotePrintDocument>[1];
  printVariant?: 'preprinted' | 'letterhead';
  onClose?: () => void;
}) {
  const model = buildDeliveryNotePrintDocument(props.data, props.companyInfo, {
    letterhead: props.printVariant !== 'preprinted',
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EnginePackingListPrint(props: {
  data: Parameters<typeof buildPackingListPrintDocument>[0];
  companyInfo?: Parameters<typeof buildPackingListPrintDocument>[1];
  printVariant?: 'preprinted' | 'letterhead';
  onClose?: () => void;
}) {
  const model = buildPackingListPrintDocument(props.data, props.companyInfo, {
    letterhead: props.printVariant !== 'preprinted',
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineCommercialInvoicePrint(props: {
  data: Parameters<typeof buildCommercialInvoicePrintDocument>[0];
  companyInfo?: Parameters<typeof buildCommercialInvoicePrintDocument>[1];
  printVariant?: 'preprinted' | 'letterhead';
  onClose?: () => void;
}) {
  const model = buildCommercialInvoicePrintDocument(props.data, props.companyInfo, {
    letterhead: props.printVariant !== 'preprinted',
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EnginePurchaseOrderPrint(props: {
  po: Parameters<typeof buildPurchaseOrderPrintDocument>[0];
  items: Parameters<typeof buildPurchaseOrderPrintDocument>[1];
  companyInfo?: Parameters<typeof buildPurchaseOrderPrintDocument>[2];
  approvalSigners?: Parameters<typeof buildPurchaseOrderPrintDocument>[3];
  spotPurchaserName?: string | null;
  onClose?: () => void;
}) {
  const model = buildPurchaseOrderPrintDocument(
    props.po,
    props.items,
    props.companyInfo,
    props.approvalSigners,
    { spotPurchaserName: props.spotPurchaserName }
  );
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineSarPrint(props: {
  data: Parameters<typeof buildSarPrintDocument>[0];
  companyInfo?: Parameters<typeof buildSarPrintDocument>[1];
  approvalSigners?: Parameters<typeof buildSarPrintDocument>[2];
  onClose?: () => void;
}) {
  const model = buildSarPrintDocument(props.data, props.companyInfo, props.approvalSigners);
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineSalesInvoicePrint(props: {
  invoice: Parameters<typeof buildSalesInvoicePrintDocument>[0];
  items: Parameters<typeof buildSalesInvoicePrintDocument>[1];
  companyInfo?: Parameters<typeof buildSalesInvoicePrintDocument>[2];
  printVariant?: 'preprinted' | 'letterhead';
  revenueServiceTypeLabel?: string;
  approvalSigners?: import('@/services/approval.service').ApprovalPrintSigner[];
  onClose?: () => void;
}) {
  const model = buildSalesInvoicePrintDocument(props.invoice, props.items, props.companyInfo, {
    letterhead: props.printVariant !== 'preprinted',
    revenueServiceTypeLabel: props.revenueServiceTypeLabel,
    approvalSigners: props.approvalSigners,
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineCprPrint(props: {
  data: Parameters<typeof buildCprPrintDocument>[0];
  customerName?: string;
  printVariant?: 'preprinted' | 'letterhead';
  onClose?: () => void;
}) {
  const model = buildCprPrintDocument(props.data, {
    customerName: props.customerName,
    letterhead: props.printVariant !== 'preprinted',
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineCprLineLabelPrint(props: {
  receipt: Parameters<typeof buildCprLineLabelDocument>[0]['receipt'];
  line: Parameters<typeof buildCprLineLabelDocument>[0]['line'];
  customerName?: string;
  onClose?: () => void;
}) {
  const model = buildCprLineLabelDocument({
    receipt: props.receipt,
    line: props.line,
    customerName: props.customerName,
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineCspmRequirementPrint(props: {
  data: Parameters<typeof buildRequirementPrintDocument>[0];
  customerName?: string;
  printVariant?: 'preprinted' | 'letterhead';
  onClose?: () => void;
}) {
  const model = buildRequirementPrintDocument(props.data, {
    customerName: props.customerName,
    letterhead: props.printVariant !== 'preprinted',
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineCspmReturnPrint(props: {
  data: Parameters<typeof buildReturnPrintDocument>[0];
  customerName?: string;
  printVariant?: 'preprinted' | 'letterhead';
  onClose?: () => void;
}) {
  const model = buildReturnPrintDocument(props.data, {
    customerName: props.customerName,
    letterhead: props.printVariant !== 'preprinted',
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}

export function EngineCspmReconciliationPrint(props: {
  data: Parameters<typeof buildReconciliationPrintDocument>[0];
  customerName?: string;
  documentNumber?: string;
  printVariant?: 'preprinted' | 'letterhead';
  onClose?: () => void;
}) {
  const model = buildReconciliationPrintDocument(props.data, {
    customerName: props.customerName,
    documentNumber: props.documentNumber,
    letterhead: props.printVariant !== 'preprinted',
  });
  return <GenericPaginatedDocument model={model} onClose={props.onClose} />;
}
